<template>
  <div class="tg-page-root min-h-screen bg-white dark:bg-gray-950">
    <!-- Header Section -->
    <PageHeader :title="$t('PageTitle_My_Orders')" @back="goBack" />

    <div
      class="page-filter-sticky mobile-frame-fixed-top border-b border-gray-100 dark:border-gray-800"
      style="--fixed-header-spacer-height: 64px;"
    >
      <div
        ref="tabsScrollRef"
        class="page-filter-tabs orders-filter-tabs px-4 h-full"
      >
        <button
          v-for="status in statusTabs"
          :key="status.value"
          type="button"
          class="page-filter-tab"
          :data-active="activeStatus === status.value ? 'true' : 'false'"
          @click="changeStatus(status.value)"
        >
          {{ status.label }}
        </button>
      </div>
    </div>
    <div class="page-filter-spacer" aria-hidden="true"></div>

    <div class="px-4 py-4">
      <!-- Orders List -->
      <div class="space-y-4">
        <div
          v-for="order in orders"
          :key="order.OrderNo"
          class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 shadow-sm"
        >
          <div class="flex items-start justify-between gap-2 min-w-0">
            <div class="flex items-center gap-4 min-w-0 flex-1">
              <div class="relative w-16 h-12 flex-shrink-0">
                <div
                  :class="`flag-icon-${getFlagCountryCode(order.CountryRegionCode).toLowerCase()}`"
                  class="w-full h-full rounded-xl shadow-md overflow-hidden bg-gray-100 dark:bg-gray-800"
                  style="background-size: contain; background-position: center; background-repeat: no-repeat;"
                ></div>
              </div>
              <div class="min-w-0 flex-1">
                <div class="text-base font-semibold text-gray-800 dark:text-gray-100 break-words">
                  {{ getOrderTitle(order) }}
                </div>
                <div class="text-orange-500 font-bold mt-1">
                  ${{ order.PriceUSD }}
                </div>
              </div>
            </div>
            <div :class="statusClass(order.StatusCode)" class="text-sm font-semibold flex-shrink-0 text-right max-w-[6.5rem] break-words">
              {{ statusLabel(order.StatusCode) }}
            </div>
          </div>

          <!-- 订单号 -->
          <div class="mt-3 flex items-center gap-2 min-w-0">
            <span class="text-xs text-gray-400 dark:text-gray-300 flex-shrink-0">{{ $t('Orders_OrderNo_Label') }}</span>
            <span class="text-xs font-mono text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 px-2 py-0.5 rounded truncate min-w-0 max-w-full">{{ order.OrderNo }}</span>
            <button
              type="button"
              class="p-1 rounded text-gray-400 dark:text-gray-300 hover:text-brand hover:bg-brand/10 active:scale-95 transition-all flex-shrink-0"
              :aria-label="$t('Copy')"
              @click.stop="copyToClipboard(order.OrderNo)"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>

          <div class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-sm text-gray-500 dark:text-gray-300">
            <span class="text-xs">{{ order.PaymentTime ? order.PaymentTime : order.CreateTime }}</span>
            <button
              v-if="order.StatusCode === 1 || order.StatusCode === 3"
              class="text-brand border border-brand/50 px-3 py-1 rounded-full text-xs font-medium hover:bg-brand/10 active:scale-95 transition"
              @click="openEsimDetail(order)"
            >
              {{ $t('Orders_Esim_View_Code') }}
            </button>
            <button
              v-else-if="order.StatusCode === 0"
              class="text-white bg-brand border border-brand px-3 py-1 rounded-full text-xs font-medium hover:bg-brand/90 active:scale-95 transition shadow-sm shadow-brand/20"
              @click="goToPay(order)"
            >
              {{ $t('Payment_Go_Pay') }}
            </button>
          </div>
        </div>
      </div>

      <div ref="loadMoreRef" class="h-1"></div>
      <div v-if="loading" class="py-4 text-center text-sm text-gray-400 dark:text-gray-300">{{ $t('Orders_Loading') }}</div>
      <div v-else-if="!hasMore && orders.length > 0" class="py-4 text-center text-sm text-gray-400 dark:text-gray-300">
        {{ $t('Orders_NoMore') }}
      </div>
      <div v-else-if="orders.length === 0 && !loading" class="py-16 text-center text-gray-400 dark:text-gray-300">
        {{ $t('Orders_Empty') }}
      </div>
    </div>

    <transition name="fade">
      <div
        v-if="esimDialogVisible"
        class="esim-sheet-overlay fixed inset-0 z-[85] bg-black/45"
        @click.self="closeEsimSheet"
      ></div>
    </transition>
    <transition name="slide-up">
      <div
        v-if="esimDialogVisible"
        class="esim-sheet-wrap mobile-frame-fixed tg-bottom-sheet-anchor fixed bottom-0 z-[90]"
      >
        <div class="tg-bottom-sheet-panel esim-sheet-panel mx-auto w-full max-w-[430px] overflow-hidden rounded-t-3xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-2xl">
          <div class="flex justify-center pt-2.5 pb-2 flex-shrink-0">
            <div class="h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600"></div>
          </div>
          <div class="esim-dialog-header flex items-center justify-between gap-3 px-4 pb-2 flex-shrink-0">
            <span class="text-base font-semibold text-gray-800 dark:text-gray-100 truncate min-w-0 flex-1">{{ esimSheetTitle }}</span>
            <button
              type="button"
              aria-label="close"
              class="flex-shrink-0 p-2 rounded-full text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-95 transition-all"
              @click="closeEsimSheet"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="esim-dialog-body px-4 pb-3 flex-1 min-h-0 overflow-y-auto tg-sheet-scroll">
        <div class="esim-install-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            class="esim-install-tab"
            :class="{ 'esim-install-tab--active': esimInstallTab === 'qr' }"
            :aria-selected="esimInstallTab === 'qr'"
            @click="esimInstallTab = 'qr'"
          >
            {{ $t('Orders_Esim_Tab_Scan_QR') }}
          </button>
          <button
            type="button"
            role="tab"
            class="esim-install-tab"
            :class="{ 'esim-install-tab--active': esimInstallTab === 'manual' }"
            :aria-selected="esimInstallTab === 'manual'"
            @click="esimInstallTab = 'manual'"
          >
            {{ $t('Orders_Esim_Tab_Manual_SMDP') }}
          </button>
        </div>

        <div v-if="esimInstallTab === 'qr'" class="qr-section mt-3">
          <p class="qr-section-label text-center text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
            {{ $t('Orders_Esim_QR_Scan_Hint') }}
          </p>
          <div class="qr-card">
            <div class="qr-card-inner">
              <img
                v-if="esimQrUrl"
                :src="esimQrUrl"
                alt="eSIM QR"
                class="qr-image"
              />
              <div
                v-else
                class="qr-placeholder"
              >
                <div
                  v-if="esimDetailLoading"
                  class="w-8 h-8 border-2 border-brand/40 border-t-brand rounded-full animate-spin"
                ></div>
                <span class="text-xs text-gray-500 dark:text-gray-300 mt-2">
                  {{ esimDetailLoading ? $t('Orders_Esim_QR_Generating') : $t('Orders_Esim_QR_Empty') }}
                </span>
              </div>
            </div>
          </div>
          <p class="qr-section-note text-center text-xs text-gray-400 dark:text-gray-300 mt-2">
            {{ $t('Orders_Esim_QR_Once_Hint') }}
          </p>
        </div>

        <div v-else class="mt-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 px-3 py-3">
          <p class="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">
            {{ $t('Orders_Esim_Manual_Section_Title') }}
          </p>
          <div v-if="hasManualInstall" class="space-y-2.5">
            <div class="esim-copy-row">
              <span class="text-xs text-gray-500 dark:text-gray-300 flex-shrink-0">{{ $t('Orders_Esim_Label_Smdp_Address') }}</span>
              <div class="flex items-center gap-1.5 min-w-0 flex-1 justify-end">
                <span class="text-xs font-medium text-gray-800 dark:text-gray-100 truncate" :title="esimLpaManual.smdpAddress">
                  {{ esimLpaManual.smdpAddress }}
                </span>
                <button
                  type="button"
                  class="flex-shrink-0 p-1.5 rounded-lg text-brand hover:bg-brand/10 active:scale-95 transition-all"
                  :aria-label="$t('Copy')"
                  @click.stop="copyToClipboard(esimLpaManual.smdpAddress)"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
            <div class="esim-copy-row">
              <span class="text-xs text-gray-500 dark:text-gray-300 flex-shrink-0">{{ $t('Orders_Esim_Label_Activation_Code') }}</span>
              <div class="flex items-center gap-1.5 min-w-0 flex-1 justify-end">
                <span class="text-xs font-medium text-gray-800 dark:text-gray-100 truncate" :title="esimLpaManual.activationCode">
                  {{ esimLpaManual.activationCode }}
                </span>
                <button
                  type="button"
                  class="flex-shrink-0 p-1.5 rounded-lg text-brand hover:bg-brand/10 active:scale-95 transition-all"
                  :aria-label="$t('Copy')"
                  @click.stop="copyToClipboard(esimLpaManual.activationCode)"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
            <div v-if="esimLpaManual.confirmationCode" class="esim-copy-row">
              <span class="text-xs text-gray-500 dark:text-gray-300 flex-shrink-0">{{ $t('Orders_Esim_Label_Confirmation_Code') }}</span>
              <div class="flex items-center gap-1.5 min-w-0 flex-1 justify-end">
                <span class="text-xs font-medium text-gray-800 dark:text-gray-100 truncate" :title="esimLpaManual.confirmationCode">
                  {{ esimLpaManual.confirmationCode }}
                </span>
                <button
                  type="button"
                  class="flex-shrink-0 p-1.5 rounded-lg text-brand hover:bg-brand/10 active:scale-95 transition-all"
                  :aria-label="$t('Copy')"
                  @click.stop="copyToClipboard(esimLpaManual.confirmationCode)"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <p v-else class="text-xs text-gray-500 dark:text-gray-300 leading-relaxed">
            {{ esimDetailLoading ? $t('Orders_Esim_QR_Generating') : $t('Orders_Esim_Manual_Unavailable') }}
          </p>
        </div>

        <div class="mt-3 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 px-3 py-2.5">
          <div class="flex items-center justify-between gap-3">
            <span class="text-xs text-gray-500 dark:text-gray-300">{{ $t('Orders_Esim_Status_Label') }}</span>
            <span class="esim-status-badge text-xs font-semibold px-2 py-0.5 rounded-full bg-brand/10 text-brand">
              {{ esimStatusText }}
            </span>
          </div>
          <div class="mt-2 flex items-center justify-between gap-3">
            <span class="text-xs text-gray-500 dark:text-gray-300">{{ $t('Orders_Esim_Label_Data') }}</span>
            <span class="text-xs font-semibold text-gray-800 dark:text-gray-100 text-right">
              {{ esimPlanDataText }}
            </span>
          </div>
          <div class="mt-2 flex items-center justify-between gap-3">
            <span class="text-xs text-gray-500 dark:text-gray-300">{{ $t('Orders_Esim_Label_Service_Days') }}</span>
            <span class="text-xs font-semibold text-gray-800 dark:text-gray-100 text-right">
              {{ esimPlanDaysText }}
            </span>
          </div>
          <div v-if="esimPhoneNumber" class="mt-2 flex items-center justify-between gap-3">
            <span class="text-xs text-gray-500 dark:text-gray-300">{{ $t('Orders_Esim_Label_PhoneNumber') }}</span>
            <div class="flex items-center gap-1.5 min-w-0 flex-1 justify-end">
              <span class="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate" :title="esimPhoneNumber">
                {{ esimPhoneNumber }}
              </span>
              <button
                type="button"
                class="flex-shrink-0 p-1.5 rounded-lg text-brand hover:bg-brand/10 active:scale-95 transition-all"
                :aria-label="$t('Copy')"
                @click.stop="copyPhoneNumber"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Telegram 社群入口：保持常駐，不折疊 -->
        <button
          type="button"
          class="w-full mt-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 px-3 py-2.5 flex items-center justify-between gap-3 active:scale-[0.98] transition-all"
          @click="openTelegramCommunity"
        >
          <span class="flex items-center gap-2.5 min-w-0">
            <span class="w-8 h-8 rounded-full bg-white dark:bg-gray-900 flex items-center justify-center shadow-sm flex-shrink-0">
              <img :src="tgIcon" alt="Telegram" class="w-5 h-5 object-contain" />
            </span>
            <span class="text-left min-w-0">
              <span class="block text-xs font-semibold text-gray-700 dark:text-gray-300 truncate">{{ $t('tgGroup1') }}</span>
              <span class="block text-[11px] text-gray-400 dark:text-gray-300 truncate">{{ $t('Contact_Join_Community') }}</span>
            </span>
          </span>
          <svg class="w-3.5 h-3.5 text-gray-400 dark:text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <!-- 詳細資料：默認折疊，避免手機端信息過載 -->
        <div
          class="esim-accordion mt-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 overflow-hidden"
          :class="{ 'esim-accordion--open': esimDetailsOpen }"
        >
          <button
            type="button"
            class="esim-accordion-trigger px-3 py-2.5 text-xs font-semibold text-gray-700 dark:text-gray-200"
            :aria-expanded="esimDetailsOpen"
            @click="toggleEsimSection('details')"
          >
            <span>{{ $t('Invitation_Details') }}</span>
            <span class="esim-accordion-chevron" aria-hidden="true" />
          </button>
          <div class="esim-accordion-panel">
            <div class="esim-accordion-panel-inner border-t border-gray-100 dark:border-gray-800">
              <div class="px-3 py-2 flex items-center justify-between gap-2 min-h-[40px]">
                <span class="text-xs text-gray-500 dark:text-gray-300 flex-shrink-0">{{ $t('Orders_Esim_Label_Iccid') }}</span>
                <div class="flex items-center gap-1.5 min-w-0 flex-1 justify-end">
                  <span class="text-xs font-medium text-gray-800 dark:text-gray-100 truncate" :title="esimDetail?.iccid || '--'">
                    {{ esimDetail?.iccid || '--' }}
                  </span>
                  <button
                    v-if="esimDetail?.iccid"
                    type="button"
                    class="flex-shrink-0 p-1.5 rounded-lg text-brand hover:bg-brand/10 active:scale-95 transition-all"
                    :aria-label="$t('Copy')"
                    @click.stop="copyToClipboard(esimDetail.iccid)"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
              <div
                v-if="esimPhoneNumber"
                class="px-3 py-2 flex items-center justify-between gap-2 min-h-[40px] border-t border-gray-100 dark:border-gray-800"
              >
                <span class="text-xs text-gray-500 dark:text-gray-300 flex-shrink-0">{{ $t('Orders_Esim_Label_PhoneNumber') }}</span>
                <div class="flex items-center gap-1.5 min-w-0 flex-1 justify-end">
                  <span class="text-xs font-medium text-gray-800 dark:text-gray-100 truncate" :title="esimPhoneNumber">
                    {{ esimPhoneNumber }}
                  </span>
                  <button
                    type="button"
                    class="flex-shrink-0 p-1.5 rounded-lg text-brand hover:bg-brand/10 active:scale-95 transition-all"
                    :aria-label="$t('Copy')"
                    @click.stop="copyPhoneNumber"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
              <div class="px-3 py-2 flex items-center justify-between gap-2 min-h-[40px] border-t border-gray-100 dark:border-gray-800">
                <span class="text-xs text-gray-500 dark:text-gray-300 flex-shrink-0">{{ $t('Orders_Esim_Label_OrderNo') }}</span>
                <div class="flex items-center gap-1.5 min-w-0 flex-1 justify-end">
                  <span class="text-xs font-medium text-gray-800 dark:text-gray-100 truncate" :title="esimDetail?.orderNo || '--'">
                    {{ esimDetail?.orderNo || '--' }}
                  </span>
                  <button
                    v-if="esimDetail?.orderNo"
                    type="button"
                    class="flex-shrink-0 p-1.5 rounded-lg text-brand hover:bg-brand/10 active:scale-95 transition-all"
                    :aria-label="$t('Copy')"
                    @click.stop="copyToClipboard(esimDetail.orderNo)"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-3 rounded-xl bg-brand/5 border border-brand/20 px-3 py-3">
          <p class="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">
            {{ esimInstallTab === 'manual' ? $t('Orders_Esim_Manual_Steps_Title') : $t('Orders_Esim_QR_Steps_Title') }}
          </p>
          <ul class="space-y-1.5 text-xs text-gray-600 dark:text-gray-300 leading-snug">
            <li v-for="(stepKey, index) in esimInstallStepKeys" :key="`${esimInstallTab}-${index}`">
              {{ $t(stepKey) }}
            </li>
          </ul>
        </div>
          </div>
          <div class="esim-sheet-footer flex-shrink-0 px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
            <button
              type="button"
              class="esim-close-action w-full py-3 rounded-xl font-semibold text-brand border border-brand/50 bg-white dark:bg-gray-900 hover:bg-brand/5 active:scale-[0.98] transition-all"
              @click="closeEsimSheet"
            >
              {{ $t('Orders_Esim_Close') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="payMethodSheetVisible"
        class="fixed inset-0 z-[85] bg-black/45"
        @click.self="cancelPayMethodSheet"
      ></div>
    </transition>
    <transition name="slide-up">
      <div
        v-if="payMethodSheetVisible"
        class="pay-method-sheet-wrap mobile-frame-fixed tg-bottom-sheet-anchor fixed bottom-0 z-[90]"
      >
        <div class="pay-method-sheet-panel mx-auto w-full max-w-[430px] overflow-hidden rounded-t-3xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-2xl">
          <div class="flex justify-center pt-2.5 pb-2">
            <div class="h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600"></div>
          </div>
          <div class="px-4 pb-2">
            <h3 class="text-base font-semibold text-gray-900 dark:text-gray-100">
              {{ $t('Payment_Method_Label') }}
            </h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-300">
              {{ $t('Payment_Method_Pick_Message') }}
            </p>
          </div>
          <div class="px-4 pb-4 max-h-[min(52vh,420px)] overflow-y-auto tg-sheet-scroll space-y-2">
            <button
              v-for="method in payMethodOptions"
              :key="method"
              type="button"
              class="pay-method-option w-full text-left rounded-2xl border border-gray-200 dark:border-gray-700 px-4 py-3 transition active:scale-[0.98]"
              @click="confirmPayWithMethod(method)"
            >
              <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {{ getPaymentMethodTitle(method, t) }}
              </div>
              <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-300">
                {{ getPaymentMethodDesc(method, t, { starsAmount: getStarsAmountForOrder(payTargetOrder) }) }}
              </div>
            </button>
          </div>
          <div class="px-4 pb-5 pt-1 border-t border-gray-100 dark:border-gray-800">
            <button
              type="button"
              class="w-full rounded-2xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-300"
              @click="cancelPayMethodSheet"
            >
              {{ $t('Payment_Cancel') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, inject, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/PageHeader.vue'
import { useTelegramWebApp } from '@/composables/useTelegramWebApp'
import { useBackButton } from '@/composables/useBackButton'
import { showAppNotification } from '@/utils/appNotification'
import { writeAppClipboard } from '@/utils/appClipboard'
import {
  executeOrderPayment,
  estimateStarsFromUsd,
  getPaymentMethodDesc,
  getPaymentMethodTitle,
  isAixPaymentMethod,
  isSameAixPaymentMethod,
  PAYMENT_METHOD_COBO,
  PAYMENT_METHOD_STARS,
  pickPaymentMethod,
} from '@/utils/orderPaymentFlow'
import { syncTelegramBackButton } from '@/utils/telegramBackButton'
import { getCountryRegionDisplayName, getFlagCountryCode } from '@/utils/countryRegionDisplay'
import { formatMsisdn, formatMsisdnCopy } from '@/utils/msisdn'
import { parseLpaString } from '@/utils/parseLpa'
import { isTelegramIosPlatform } from '@/utils/telegramViewport'
import logger from '@/utils/logger'

const IOS_QR_INSTALL_STEPS = [
  'Orders_Esim_Install_QR_iOS_Step1',
  'Orders_Esim_Install_QR_iOS_Step2',
  'Orders_Esim_Install_QR_iOS_Step3',
  'Orders_Esim_Install_QR_iOS_Step4',
  'Orders_Esim_Install_QR_iOS_Step5',
  'Orders_Esim_Install_QR_iOS_Step6',
  'Orders_Esim_Install_QR_iOS_Step7'
]

const IOS_MANUAL_INSTALL_STEPS = [
  'Orders_Esim_Install_Manual_iOS_Step1',
  'Orders_Esim_Install_Manual_iOS_Step2',
  'Orders_Esim_Install_Manual_iOS_Step3',
  'Orders_Esim_Install_Manual_iOS_Step4',
  'Orders_Esim_Install_Manual_iOS_Step5',
  'Orders_Esim_Install_Manual_iOS_Step6',
  'Orders_Esim_Install_Manual_iOS_Step7',
  'Orders_Esim_Install_Manual_iOS_Step8',
  'Orders_Esim_Install_Manual_iOS_Step9'
]

const GENERIC_QR_INSTALL_STEPS = [
  'Orders_Esim_Install_Step1',
  'Orders_Esim_Install_Step2'
]

const GENERIC_MANUAL_INSTALL_STEPS = [
  'Orders_Esim_Install_Manual_Generic_Step1',
  'Orders_Esim_Install_Manual_Generic_Step2',
  'Orders_Esim_Install_Manual_Generic_Step3'
]

const router = useRouter()
const route = useRoute()
const { setupBackButton } = useBackButton({ autoSetup: false })
const api = inject('api')
const timeService = inject('timeService')
const { t, locale } = useI18n()
const tgIcon = require('@/assets/icons/tg.png')

const activeStatus = ref('')
const orders = ref([])
const total = ref(0)
const pageIndex = ref(1)
const pageSize = 10
const loading = ref(false)
const loadMoreRef = ref(null)
const tabsScrollRef = ref(null)
let loadMoreObserver = null
const esimDialogVisible = ref(false)
const payMethodSheetVisible = ref(false)
const payMethodOptions = ref([])
const payTargetOrder = ref(null)
let payMethodSheetResolve = null
const esimDetailsOpen = ref(false)
const esimInstallTab = ref('qr')
const esimTargetOrder = ref(null)
const esimDetailLoading = ref(false)
const esimDetail = ref(null)
const esimQrUrl = ref('')
const esimUsageLoading = ref(false)
const esimUsage = ref(null)

const statusTabs = computed(() => [
  { label: t('Orders_Tab_All'), value: '' },
  { label: t('Orders_Tab_Paid'), value: 1 },
  { label: t('Orders_Tab_Unpaid'), value: 0 },
  { label: t('Orders_Status_Gifted'), value: 3 },
  { label: t('Orders_Tab_Invalid'), value: 2 },
  { label: t('Orders_Tab_Refunded'), value: 9 }
])

const hasMore = computed(() => orders.value.length < total.value)
const esimStatusText = computed(() => {
  const status = esimUsage.value?.status || esimDetail.value?.orderStatus
  if (!status) {
    return '--'
  }
  const map = {
    NOTACTIVE: t('Orders_Esim_Status_NotActive'),
    ACTIVATED: t('Orders_Esim_Status_Activated'),
    INUSE: t('Orders_Esim_Status_InUse'),
    USED: t('Orders_Esim_Status_Used'),
    EXPIRED: t('Orders_Esim_Status_Expired'),
    GOT_RESOURCE: t('Orders_Esim_Status_NotActive'),
    IN_USE: t('Orders_Esim_Status_InUse'),
    USED_UP: t('Orders_Esim_Status_DataUsedUp'),
    UNUSED_EXPIRED: t('Orders_Esim_Status_ExpiredUnused'),
    USED_EXPIRED: t('Orders_Esim_Status_ExpiredUsed'),
    CANCEL: t('Orders_Esim_Status_Canceled')
  }
  return map[status] || "--"
})
const usageScope = computed(() => {
  if (esimUsage.value?.usageScope) {
    return esimUsage.value.usageScope
  }
  return Number(esimUsage.value?.planType) === 1 ? 'daily' : 'total'
})
const hasUsageTotal = computed(() => {
  return Number.isFinite(Number(esimUsage.value?.dataTotalBytes)) && Number(esimUsage.value?.dataTotalBytes) > 0
})
const hasUsageData = computed(() => {
  if (hasUsageTotal.value) {
    return true
  }
  const used = Number(esimUsage.value?.dataUsedBytes)
  return usageScope.value === 'daily_unlimited' && Number.isFinite(used) && used >= 0
})
const usagePercent = computed(() => {
  if (!hasUsageTotal.value) {
    return 0
  }
  const used = Number(esimUsage.value?.dataUsedBytes) || 0
  const total = Number(esimUsage.value?.dataTotalBytes)
  return Math.min(100, Math.max(0, Math.round((used / total) * 100)))
})
const usageSummaryText = computed(() => {
  if (!hasUsageData.value) {
    return '--'
  }
  const used = formatBytes(esimUsage.value?.dataUsedBytes)
  if (usageScope.value === 'daily_unlimited') {
    return t('Orders_Esim_Usage_Daily_Used_Only', { used })
  }
  const total = formatBytes(esimUsage.value?.dataTotalBytes)
  if (usageScope.value === 'daily') {
    return t('Orders_Esim_Usage_Summary_Daily', { used, total })
  }
  return t('Orders_Esim_Usage_Summary', { used, total })
})
const usageLastSyncText = computed(() => {
  const lastSyncTime = esimUsage.value?.lastSyncTime
  if (!lastSyncTime) {
    return '--'
  }
  return timeService?.convertToLocalTime?.(lastSyncTime) || lastSyncTime
})
const esimPhoneNumber = computed(() => formatMsisdn(esimDetail.value?.msisdn))
const isIosPlatform = computed(() => isTelegramIosPlatform())
const esimLpaManual = computed(() => parseLpaString(esimDetail.value?.downloadUrl))
const hasManualInstall = computed(() => !!esimLpaManual.value)
const esimSheetTitle = computed(() => (
  isIosPlatform.value ? t('Orders_Esim_Install_Title_iOS') : t('Orders_Esim_Install_Title')
))
const esimPlanDataText = computed(() => {
  const gb = esimTargetOrder.value?.GB
  if (gb == null || gb === '') {
    return '--'
  }
  const num = Number(gb)
  return Number.isFinite(num) ? `${num.toFixed(2)} GB` : `${gb} GB`
})
const esimPlanDaysText = computed(() => {
  const days = esimTargetOrder.value?.ValidityDays
  if (days == null || days === '') {
    return '--'
  }
  return t('Orders_Esim_Service_Days_Value', { days })
})
const esimInstallStepKeys = computed(() => {
  if (esimInstallTab.value === 'manual') {
    return isIosPlatform.value ? IOS_MANUAL_INSTALL_STEPS : GENERIC_MANUAL_INSTALL_STEPS
  }
  return isIosPlatform.value ? IOS_QR_INSTALL_STEPS : GENERIC_QR_INSTALL_STEPS
})

const copyPhoneNumber = () => {
  copyToClipboard(formatMsisdnCopy(esimDetail.value?.msisdn))
}

const goBack = () => {
  // 检查是否有历史记录，如果有则返回，否则跳转到首页
  // 也可以根据 window.history.length 来判断，但为了安全起见，
  // 如果是从登录页跳转过来的（可能是因为未登录重定向），
  // 直接 router.back() 可能会再次触发守卫重定向循环（虽然我们在 LoginView 做了处理）。
  // 最稳妥的方式是：如果上一页是登录页，或者没有上一页，则跳转到首页或个人中心。
  
  // 简单处理：直接跳转到个人中心，因为订单页面通常是从个人中心进入的
  router.push('/account')
}

const getOrderDisplayTime = (order) => order?.PaymentTime || order?.CreateTime || ''

const sortOrdersByTimeDesc = (list) => {
  return [...list].sort((a, b) => getOrderDisplayTime(b).localeCompare(getOrderDisplayTime(a)))
}

const fetchOrders = async (reset = false) => {
  if (loading.value) {
    return
  }
  loading.value = true
  try {
    const params = {
      page_index: pageIndex.value,
      page_size: pageSize
    }
    if (activeStatus.value !== '') {
      params.status_code = activeStatus.value
    }
    const res = await api.order.queryOrderList(params)
    if (res?.data?.status !== '0') {
      showAppNotification({
        message: res?.data?.message,
        type: 'error',
        duration: 2500,
        position: 'bottom-right'
      })
      return
    }
    const data = res?.data?.data
    // Handle old format: array of {StatusCode, Data} groups
    let list = []
    if (Array.isArray(data)) {
      // Format: [{StatusCode: 0, Data: [...]}, {StatusCode: 1, Data: [...]}, ...]
      if (activeStatus.value === '') {
        // Show all: flatten all groups into a single array
        list = data.flatMap(group => {
          if (group && Array.isArray(group.Data)) {
            return group.Data
          }
          return []
        })
        // Calculate total from all groups
        total.value = data.reduce((sum, group) => {
          return sum + (Array.isArray(group.Data) ? group.Data.length : 0)
        }, 0)
      } else {
        // Show specific status: backend already filtered, should have at most one group
        // Find the matching group or use the first one (if backend filtered correctly)
        const targetGroup = data.find(group => group.StatusCode === activeStatus.value) || data[0]
        if (targetGroup && Array.isArray(targetGroup.Data)) {
          list = targetGroup.Data
          // For pagination: estimate total based on current page size
          // If we got a full page, assume there might be more
          const currentTotal = reset ? 0 : orders.value.length
          if (list.length >= pageSize) {
            // Assume there might be more (set total to current + pageSize + 1 to enable load more)
            total.value = currentTotal + list.length + 1
          } else {
            // This is likely the last page
            total.value = currentTotal + list.length
          }
        } else {
          list = []
          const currentTotal = reset ? 0 : orders.value.length
          total.value = currentTotal
        }
      }
    } else if (data && Array.isArray(data.data)) {
      // Fallback: handle new pagination format if it exists
      list = data.data
      total.value = typeof data.total === 'number' ? data.total : list.length
    } else {
      list = []
      total.value = 0
    }

    list = sortOrdersByTimeDesc(list)
    
    if (reset) {
      orders.value = list
    } else {
      orders.value = sortOrdersByTimeDesc([...orders.value, ...list])
    }
    if (list.length > 0) {
      pageIndex.value += 1
    }
  } catch (error) {
    logger.error(t('Orders_Fetch_Failed'), error)
    showAppNotification({
      message: t('Orders_Fetch_Failed'),
      type: 'error',
      duration: 3000,
      position: 'bottom-right'
    })
  } finally {
    loading.value = false
    tryAutoLoadMore()
  }
}

const changeStatus = (value) => {
  activeStatus.value = value
  pageIndex.value = 1
  total.value = 0
  orders.value = []
  scrollActiveTabIntoView()
  fetchOrders(true)
}

const scrollActiveTabIntoView = () => {
  nextTick(() => {
    const container = tabsScrollRef.value
    if (!container) return
    const activeTab = container.querySelector('[data-active="true"]')
    if (!activeTab) return

    const tabLeft = activeTab.offsetLeft
    const tabWidth = activeTab.offsetWidth
    const containerWidth = container.clientWidth
    const targetLeft = tabLeft - (containerWidth - tabWidth) / 2
    container.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: 'smooth',
    })
  })
}

const tryAutoLoadMore = async () => {
  if (loading.value || !hasMore.value) {
    return
  }
  await nextTick()
  if (!loadMoreRef.value) {
    return
  }
  const rect = loadMoreRef.value.getBoundingClientRect()
  if (rect.top <= window.innerHeight + 120) {
    fetchOrders()
  }
}

const buildQrUrl = (text) => {
  if (!text) {
    return ''
  }
  return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(text)}`
}

const copyToClipboard = async (text) => {
  if (!text) return
  const copied = await writeAppClipboard(text)
  if (copied) {
    showAppNotification({
      message: t('Copied_Successfully'),
      type: 'success',
      duration: 2000,
      position: 'bottom-right'
    })
    return
  }
  showAppNotification({
    message: t('Orders_Esim_Fetch_Failed'),
    type: 'error',
    duration: 2000,
    position: 'bottom-right'
  })
}

const BODY_CLASS_ESIM_DIALOG = 'esim-dialog-open'

const closeEsimSheet = () => {
  esimDialogVisible.value = false
}

const resetEsimSheetState = () => {
  document.body.classList.remove(BODY_CLASS_ESIM_DIALOG)
  esimDetailsOpen.value = false
  esimInstallTab.value = 'qr'
  esimTargetOrder.value = null
  esimDetail.value = null
  esimQrUrl.value = ''
  esimUsage.value = null
  syncTelegramBackButton(route, router)
}

const toggleEsimSection = (section) => {
  if (section === 'details') {
    esimDetailsOpen.value = !esimDetailsOpen.value
  }
}

watch(esimDialogVisible, (visible) => {
  if (visible) {
    document.body.classList.add(BODY_CLASS_ESIM_DIALOG)
    setupBackButton(true, closeEsimSheet)
    return
  }
  resetEsimSheetState()
})

watch(locale, () => {
  scrollActiveTabIntoView()
})

const { openTelegramLink } = useTelegramWebApp()

const getExistingAixPaymentUrl = (order, paymentMethod) => (
  isSameAixPaymentMethod(paymentMethod, order?.PaymentMethod) ? order?.PaymentUrl : null
)

const getStarsAmountForOrder = (order) => {
  if (!order?.PriceUSD) return '—'
  return estimateStarsFromUsd(order.PriceUSD)
}

const closePayMethodSheet = () => {
  payMethodSheetVisible.value = false
  payTargetOrder.value = null
  payMethodOptions.value = []
  payMethodSheetResolve = null
}

const openPayMethodSheet = ({ methods }) => {
  return new Promise((resolve) => {
    payMethodOptions.value = methods
    payMethodSheetResolve = resolve
    payMethodSheetVisible.value = true
  })
}

const confirmPayWithMethod = (method) => {
  const resolve = payMethodSheetResolve
  closePayMethodSheet()
  resolve?.(method)
}

const cancelPayMethodSheet = () => {
  const resolve = payMethodSheetResolve
  closePayMethodSheet()
  resolve?.(null)
}

const proceedPay = async (order, paymentMethod) => {
  if (!order?.OrderNo || !paymentMethod) {
    return
  }

  const productName = getOrderTitle(order)
  const existingCoboPaymentUrl = paymentMethod === PAYMENT_METHOD_COBO ? order.PaymentUrl : null
  const existingAixPaymentUrl = getExistingAixPaymentUrl(order, paymentMethod)

  const payResult = await executeOrderPayment({
    api,
    router,
    orderNo: order.OrderNo,
    productName,
    amountUsd: order.PriceUSD,
    paymentMethod,
    t,
    existingCoboPaymentUrl,
    existingAixPaymentUrl,
  })

  if (!payResult.ok) {
    showAppNotification({
      message: payResult.message || t('Package_Pay_Process_Failed'),
      type: 'error',
      duration: 2500,
      position: 'bottom-right'
    })
    return
  }

  if (
    payResult.method === PAYMENT_METHOD_STARS
    && (payResult.invoiceStatus === 'cancelled' || payResult.invoiceStatus === 'failed')
  ) {
    showAppNotification({
      message: t('Payment_Failed_Message'),
      type: 'warning',
      duration: 2500,
      position: 'bottom-right'
    })
  }
}

const goToPay = async (order) => {
  if (!order?.OrderNo) {
    return
  }

  payTargetOrder.value = order
  const paymentMethod = await pickPaymentMethod({
    t,
    api,
    locale: locale.value,
    preferInAppPicker: true,
    onPickRequest: openPayMethodSheet,
  })

  if (!paymentMethod) {
    payTargetOrder.value = null
    return
  }

  await proceedPay(order, paymentMethod)
  payTargetOrder.value = null
}

const openTelegramCommunity = () => {
  const groupUrl = process.env.VUE_APP_TG_GROUP1
  if (!groupUrl) {
    router.push('/contact')
    return
  }
  openTelegramLink(groupUrl)
}

const openEsimDetail = async (order) => {
  const orderNo = order?.OrderNo
  if (!orderNo) {
    showAppNotification({
      message: t('Orders_Esim_OrderNo_Missing'),
      type: 'error',
      duration: 2500,
      position: 'bottom-right'
    })
    return
  }
  esimDialogVisible.value = true
  esimTargetOrder.value = order
  esimInstallTab.value = 'qr'
  esimUsage.value = null
  await Promise.allSettled([
    fetchEsimDetail(orderNo),
    fetchEsimUsage(orderNo)
  ])
}

const fetchEsimDetail = async (orderNo) => {
  // const orderNo1 = "mede-1747547545-40099"
  if (esimDetailLoading.value) {
    return
  }
  esimDetailLoading.value = true
  esimDetail.value = null
  esimQrUrl.value = ''
  try {
    const res = await api.order.getEsimDetail(orderNo)
    if (res?.data?.status !== '0') {
      showAppNotification({
        message: res?.data?.message || t('Orders_Esim_Fetch_Failed'),
        type: 'error',
        duration: 2500,
        position: 'bottom-right'
      })
      return
    }
    const data = res?.data?.data || null
    // API may return supplier orderNo; always show our internal orderNo in Details.
    esimDetail.value = data ? { ...data, orderNo } : null
    esimQrUrl.value = buildQrUrl(data?.downloadUrl)
  } catch (error) {
    logger.error(t('Orders_Esim_Fetch_Failed'), error)
    showAppNotification({
      message: t('Orders_Esim_Fetch_Failed'),
      type: 'error',
      duration: 3000,
      position: 'bottom-right'
    })
  } finally {
    esimDetailLoading.value = false
  }
}

const fetchEsimUsage = async (orderNo) => {
  if (esimUsageLoading.value) {
    return
  }
  esimUsageLoading.value = true
  esimUsage.value = null
  try {
    const res = await api.order.getEsimUsage(orderNo, {
      skipLoading: true,
      skipErrorHandler: true
    })
    if (res?.data?.status !== '0') {
      return
    }
    esimUsage.value = res?.data?.data || null
  } catch (error) {
    logger.error(t('Orders_Esim_Usage_Fetch_Failed'), error)
  } finally {
    esimUsageLoading.value = false
  }
}

const formatBytes = (value) => {
  const bytes = Number(value)
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '--'
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let size = bytes
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex += 1
  }
  const decimals = unitIndex === 0 ? 0 : size >= 10 ? 1 : 2
  return `${size.toFixed(decimals)} ${units[unitIndex]}`
}

const statusLabel = (status) => {
  const map = {
    0: t('Orders_Status_Unpaid'),
    1: t('Orders_Status_Paid'),
    2: t('Orders_Status_Invalid'),
    3: t('Orders_Status_Gifted'),
    4: t('Orders_Status_Expired'),
    9: t('Orders_Status_Refunded'),
  }
  return Object.prototype.hasOwnProperty.call(map, status) ? map[status] : t('Common_Unknown')
}

const statusClass = (status) => {
  const map = {
    0: 'text-red-500',
    1: 'text-gray-500 dark:text-gray-300',
    2: 'text-gray-400 dark:text-gray-300',
    9: 'text-gray-400 dark:text-gray-300'
  }
  return Object.prototype.hasOwnProperty.call(map, status) ? map[status] : 'text-gray-400 dark:text-gray-300'
}

const getOrderTitle = (order) => {
  return t('Orders_Title_Format', {
    country: getCountryName(order.CountryRegionCode),
    days: order.ValidityDays,
    gb: order.GB
  })
}

const getCountryName = (code) => {
  return getCountryRegionDisplayName({
    code,
    locale: locale.value,
    t
  })
}

onMounted(() => {
  fetchOrders(true)
  scrollActiveTabIntoView()
  loadMoreObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0]
      if (!entry || !entry.isIntersecting) {
        return
      }
      if (loading.value || !hasMore.value) {
        return
      }
      fetchOrders()
    },
    { root: null, rootMargin: '0px 0px 120px 0px', threshold: 0.01 }
  )
  nextTick(() => {
    if (loadMoreRef.value) {
      loadMoreObserver.observe(loadMoreRef.value)
    }
  })
})

onUnmounted(() => {
  document.body.classList.remove(BODY_CLASS_ESIM_DIALOG)
  if (loadMoreObserver) {
    loadMoreObserver.disconnect()
    loadMoreObserver = null
  }
})
</script>

<style scoped>
.flag-icon {
  background-size: contain !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.28s ease, opacity 0.28s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0.98;
}
.esim-sheet-panel {
  display: flex;
  flex-direction: column;
  height: min(88vh, calc(100dvh - 1.5rem));
  max-height: min(88vh, calc(100dvh - 1.5rem));
}
.esim-dialog-body {
  overscroll-behavior: contain;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
}
.esim-dialog-body::-webkit-scrollbar {
  display: none;
}
.esim-sheet-footer {
  padding-bottom: max(1rem, env(safe-area-inset-bottom, 0px));
}
.esim-dialog-header {
  width: 100%;
  min-width: 0;
}
.esim-status-badge {
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.esim-install-tabs {
  display: flex;
  gap: 0.5rem;
  padding: 0.25rem;
  border-radius: 0.9rem;
  background: #f3f4f6;
}
:global(html.dark .esim-install-tabs) {
  background: #111827;
}
.esim-install-tab {
  flex: 1;
  min-width: 0;
  border: 0;
  border-radius: 0.7rem;
  padding: 0.55rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  background: transparent;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
:global(html.dark .esim-install-tab) {
  color: #9ca3af;
}
.esim-install-tab--active {
  color: #111827;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
:global(html.dark .esim-install-tab--active) {
  color: #f9fafb;
  background: #1f2937;
}
.esim-copy-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  min-height: 2.25rem;
  padding: 0.35rem 0;
  border-top: 1px solid rgba(229, 231, 235, 0.9);
}
.esim-copy-row:first-child {
  border-top: 0;
  padding-top: 0;
}
:global(html.dark .esim-copy-row) {
  border-top-color: rgba(55, 65, 81, 0.9);
}

/* 二維碼區優化 */
.qr-section {
  margin-bottom: 0.25rem;
}
.qr-section-label {
  letter-spacing: 0.02em;
}
.qr-card {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  background: linear-gradient(145deg, #fafafa 0%, #f5f5f5 100%);
  border: 1px solid rgba(34, 197, 94, 0.15);
  border-radius: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8);
}
.qr-card-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  width: clamp(9.5rem, 44vw, 11rem);
  height: clamp(9.5rem, 44vw, 11rem);
  background: #fff;
  border-radius: 0.75rem;
  padding: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.qr-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  border-radius: 0.5rem;
}
.qr-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #fafafa;
  border-radius: 0.5rem;
  border: 1px dashed #e5e7eb;
}
.qr-section-note {
  letter-spacing: 0.01em;
}
.esim-accordion-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.15s ease;
}
.esim-accordion-trigger:active {
  background: rgba(107, 114, 128, 0.08);
}
.esim-accordion-chevron {
  flex-shrink: 0;
  width: 0.5rem;
  height: 0.5rem;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  opacity: 0.55;
  transform: rotate(45deg);
  transition: transform 0.26s cubic-bezier(0.22, 1, 0.36, 1);
}
.esim-accordion--open .esim-accordion-chevron {
  transform: rotate(225deg);
}
.esim-accordion-panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}
.esim-accordion--open .esim-accordion-panel {
  grid-template-rows: 1fr;
}
.esim-accordion-panel-inner {
  overflow: hidden;
  min-height: 0;
}

:global(html.dark .qr-card) {
  background: linear-gradient(145deg, #1f2937 0%, #111827 100%);
  border-color: rgba(34, 197, 94, 0.25);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

:global(html.dark .qr-card-inner) {
  background: #374151;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

:global(html.dark .qr-placeholder) {
  background: #1f2937;
  border-color: #4b5563;
}

@media (max-width: 480px) {
  .esim-sheet-panel {
    height: min(90vh, calc(100dvh - 0.75rem));
    max-height: min(90vh, calc(100dvh - 0.75rem));
  }
  .esim-dialog-body {
    padding-bottom: 0.125rem;
  }
  .qr-section-label {
    margin-bottom: 0.375rem;
    font-size: 0.8125rem;
  }
  .qr-card {
    padding: 0.625rem;
    border-radius: 0.875rem;
  }
  .qr-card-inner {
    width: clamp(8.5rem, 42vw, 10rem);
    height: clamp(8.5rem, 42vw, 10rem);
    padding: 0.625rem;
  }
  .qr-section-note {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    line-height: 1.25rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .esim-accordion-panel,
  .esim-accordion-chevron {
    transition: none;
  }
}
</style>
